import { WithFitDescription } from '@/services/preferences'
import { getProductMainPicture, placeholderImage, uploadMainImage } from '@/services/product'
import { GreenCheckmark, SlimCheckmark } from '@/components/checkmark'
import { Product as TProduct } from 'shared/types/api'
import { unique } from 'shared/utils/array'
import partition from 'lodash/partition'
import { OrderButtons } from '@/components/order-buttons'
import { useVenueContext } from '@/services/venue'
import { usePrice } from './price'
import { useAppContext } from '@/pages/_app'
import { PropsWithChildren, useRef, MouseEvent } from 'react'
import { AdminProductButton } from './admin-product-button'
import { Spinner } from './spinner'
import { ClockIcon } from './clock-icon'
import { PictureIcon } from './picture-icon'
import { EyeIcon } from './eye-icon'
import { uiEvents, filterEvents } from '@/utils/event'
import { isAllowedImageType } from 'shared/product'
import { filter, map } from 'rxjs/operators'
import { useCropper } from './cropper'
import { truly } from 'shared/utils/types'
import {
  CAME_FROM_QUERY_PARAM,
  SOURCE_PRODUCT_GROUP_NAME,
} from '@/pages/venue/[venueName]/p/[productGroupName]/[productName]'
import { useRouter } from 'next/router'

declare global {
  interface AppUIEventMap {
    ProductImageSubmitDnd: {
      type: 'ProductImageSubmitDnd'
      product: TProduct
      file: File
    }

    ProductImageSubmitInput: {
      type: 'ProductImageSubmitInput'
      product: TProduct
      file: File
    }
  }
}

export type ProductProps = {
  product: WithFitDescription<TProduct>
  sourceProductGroupName: string
  filtersUsed: boolean
  venueName: string
  orderedQuantity: number
  onQuantityChange: (quantity: number, change: '+' | '-', e: MouseEvent) => void
  allowMovePriceToOrderButton: boolean
}

export function Product(props: ProductProps) {
  const { apiClient, t } = useAppContext()
  const venueContext = useVenueContext()!
  const {
    venue,
    admin,
    venueName,
    orderManager,
    availablePreferences,
    orderTypeHasPaymentMethods,
  } = venueContext

  const { basket } = orderManager

  const { isAdminView } = admin

  const price = usePrice()

  const product = props.product

  const mainImage = getProductMainPicture(product)

  const titleWords = product.title.split(' ')
  const titleFirstWords = titleWords.slice(0, titleWords.length - 1)
  const titleLastWord = titleWords.slice(titleWords.length - 1, titleWords.length)

  const showTwoProductsInRow = venue.features.showTwoProductsInRow

  const leftOffset =
    props.orderedQuantity > 0 ? 'border-l-8 pl-2 border-action dark:border-actionDark' : 'pl-4'

  const disableButton = product.isInStopList && !admin.isAdminView

  const notFittingElements = [
    ...product.fitDescription.ingredients.map((i) => {
      return {
        ...i,
        type: 'ingredient' as const,
      }
    }),
    ...product.fitDescription.rootComponents.map((c) => {
      return {
        ...c,
        type: 'component' as const,
      }
    }),
  ]

  const [elementsCanBeRemoved, elementsCannotBeRemoved] = partition(notFittingElements, (el) => {
    return el.isRemovable
  })

  const imageRef = useRef<HTMLInputElement>(null)

  const cropperId = `productImage/${product.productGroupName}/${product.name}`

  const cropper = useCropper({
    cropperId,
    aspectRatio: 1,
    input$: uiEvents.pipe(
      filterEvents(['ProductImageSubmitDnd', 'ProductImageSubmitInput']),
      filter((event) => {
        const productUniqname = (product: TProduct) => {
          return JSON.stringify({
            name: product.name,
            productGroupName: product.productGroupName,
          })
        }
        return (
          productUniqname(event.product) === productUniqname(product) &&
          isAllowedImageType(event.file)
        )
      }),
      map((event) => {
        return event.file
      }),
    ),

    async uploadImage(event) {
      await uploadMainImage(venueName, product, event.file, event.cropBox, apiClient)
    },
  })

  const router = useRouter()

  const queryParams = new URLSearchParams({
    ...(product.kind === 'virtual'
      ? {
          [SOURCE_PRODUCT_GROUP_NAME]: props.sourceProductGroupName,
        }
      : {}),
    ...(router.route === '/venue/[venueName]/checkout'
      ? { [CAME_FROM_QUERY_PARAM]: 'checkout' }
      : {}),
  }).toString()

  const productLink =
    product.isInStopList && !admin.isAdminView
      ? {
          href: '#',
          scroll: false,
        }
      : {
          href: `/venue/${props.venueName}/p/${product.productGroupName}/${product.name}${
            queryParams ? `?${queryParams}` : ''
          }`,
        }

  const productImagePlaceholderVisibility =
    venue.features.productImagePlaceholderVisibility || 'visible'

  return (
    <div
      id={`product-${props.sourceProductGroupName}-${product.name}`}
      className={`flex ${
        showTwoProductsInRow && isAdminView
          ? `w-webAppMobileTwoColumnsAdminWidth mx-auto lg:w-full`
          : ''
      } flex-col h-full ${leftOffset} pr-4 ${product.isInStopList ? 'opacity-25' : ''}`}
    >
      <LinkWithoutPrefetch {...productLink}>
        <a className="h-full">
          <div className="flex justify-between text-lg leading-tight">
            <h3>
              {titleFirstWords.join(' ')}{' '}
              <span className="whitespace-nowrap">
                {titleLastWord}{' '}
                {props.filtersUsed && product.fitDescription.productFitsPreferences ? (
                  <GreenCheckmark className="relative inline w-4 h-4" style={{ top: '-5px' }} />
                ) : null}
              </span>
            </h3>
            {product.price > 0 && (
              <span
                className={`ml-2 flex-shrink-0 ${
                  showTwoProductsInRow && props.allowMovePriceToOrderButton
                    ? 'hidden lg:inline'
                    : ''
                }`}
              >
                {price.format(product.price)}
              </span>
            )}{' '}
          </div>
          <div className="flex flex-wrap mt-px text-sm text-opaque dark:text-opaqueDark">
            {product.volume.length > 0 && product.volumeUnit && (
              <span className="mr-2">
                {product.volume.join('/')} {product.volumeUnit}
              </span>
            )}{' '}
            <CookingTime cookingTimeMin={product.cookingTimeMin} />{' '}
            {showTwoProductsInRow &&
              props.allowMovePriceToOrderButton &&
              props.orderedQuantity > 0 &&
              product.price > 0 && <span className="lg:hidden">{price.format(product.price)}</span>}
          </div>
          <div
            className={`leading-tight whitespace-pre-line text-opaque dark:text-opaqueDark mt-2 break-words ${
              showTwoProductsInRow ? 'line-clamp-3 lg:line-clamp-none' : ''
            }`}
          >
            {product.descriptionShort}
          </div>
          {product.allergenNames && product.allergenNames.length > 0 && (
            <div className="mt-2 leading-tight text-opaque dark:text-opaqueDark">
              {t('common:contains')}{' '}
              <span
                dangerouslySetInnerHTML={{
                  __html: product.allergenNames
                    .map((allergenName) => {
                      return availablePreferences.allAllergens.find((allergen) => {
                        return allergenName === allergen.name
                      })
                    })
                    .filter(truly)
                    .map((allergen) => {
                      return t(allergen.title).toLowerCase()
                    })
                    .join(', '),
                }}
              />
            </div>
          )}

          {product.foodRestrictionNames && product.foodRestrictionNames.length > 0 && (
            <div className="flex flex-wrap mt-2 text-opaque dark:text-opaqueDark">
              {product.foodRestrictionNames
                .map((foodRestrictionName) => {
                  return availablePreferences.allFoodRestrictions.find((foodRestriction) => {
                    return foodRestrictionName === foodRestriction.name
                  })
                })
                .filter(truly)
                .map((foodRestriction) => {
                  return (
                    <div
                      key={foodRestriction.name}
                      className="flex items-center flex-shrink-0 mr-3"
                    >
                      <img
                        src={foodRestriction.imageUrl}
                        className="flex-shrink-0 inline-block h-5 mr-1"
                      />

                      <span
                        className="flex-shrink-0"
                        dangerouslySetInnerHTML={{
                          __html: t(foodRestriction.title),
                        }}
                      />
                    </div>
                  )
                })}
            </div>
          )}

          {elementsCanBeRemoved.length > 0 && elementsCannotBeRemoved.length === 0 ? (
            <div className="mt-px leading-tight text-action">
              {t('product:canBeRemoved')}{' '}
              {unique(
                elementsCanBeRemoved.map((el) => {
                  return el.title
                }),
              ).join(', ')}
            </div>
          ) : null}

          {(() => {
            const getTitles = () => {
              if (
                product.ingredients.length === 1 &&
                product.components.length === 0 &&
                elementsCannotBeRemoved[0]?.type === 'ingredient'
              ) {
                return elementsCannotBeRemoved[0].components.map((c) => {
                  return c.title
                })
              }

              return elementsCannotBeRemoved.map((el) => {
                return el.title
              })
            }
            const titles = getTitles()

            if (titles.length === 0) {
              return null
            }

            return (
              <div className="mt-px leading-tight text-negative dark:text-negativeDark">
                {t('product:canNotBeRemoved')} {unique(titles).join(', ')}
              </div>
            )
          })()}
        </a>
      </LinkWithoutPrefetch>

      <div className="flex items-start justify-center">
        {(() => {
          const placeholderClasses = mainImage
            ? ''
            : 'w-full h-30 object-cover border border-opaqueLightest bg-[#fafafa] dark:bg-backOpaqueDark'
          const whitePixel =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AAoMBgDTD2qgAAAAASUVORK5CYII='

          const width = mainImage?.width
          const height = mainImage?.height
          return (
            <div className="relative w-full mt-2">
              {cropper.isImageUploading && (
                <>
                  <div className="absolute z-10 flex items-center justify-center w-full h-full">
                    <div className="w-16">
                      <Spinner />
                    </div>
                  </div>
                  <div
                    className="absolute z-10 w-full h-full rounded-lg"
                    style={{
                      background: 'white',
                      opacity: 0.25,
                    }}
                  ></div>
                </>
              )}

              <input
                ref={imageRef}
                type="file"
                className="hidden"
                onChange={async () => {
                  const file = imageRef.current!.files?.[0]
                  if (!file) {
                    return
                  }
                  if (cropper.isImageUploading) {
                    return
                  }

                  // we need to clear out the input because the onChange won't get triggered if we
                  // submit the same file as previously (because the input doesn't "change" in that case)
                  // eslint-disable-next-line immutable/no-mutation
                  imageRef.current!.value = ''

                  uiEvents.next({
                    type: 'ProductImageSubmitInput',
                    file,
                    product,
                  })
                }}
              />

              {admin.isAdminView &&
                product.kind === 'internal' &&
                (mainImage || productImagePlaceholderVisibility === 'visible') && (
                  <div className="absolute" style={{ top: '30px', right: '20px' }}>
                    <AdminProductButton
                      onClick={() => {
                        imageRef.current!.click()
                      }}
                    >
                      <PictureIcon />
                    </AdminProductButton>
                  </div>
                )}

              <LinkWithoutPrefetch {...productLink}>
                {(() => {
                  if (!mainImage && productImagePlaceholderVisibility === 'hidden') {
                    return null
                  }

                  return (
                    <img
                      onDragOver={(event) => {
                        event.stopPropagation()
                        event.preventDefault()
                      }}
                      onDrop={async (event) => {
                        event.stopPropagation()
                        event.preventDefault()

                        if (!admin.isAdminView) {
                          return
                        }

                        if (cropper.isImageUploading) {
                          return
                        }

                        const file = event.dataTransfer?.files?.[0]
                        if (!file) {
                          return
                        }

                        uiEvents.next({
                          type: 'ProductImageSubmitDnd',
                          file,
                          product,
                        })
                      }}
                      style={
                        width && height
                          ? {
                              paddingTop: `${Math.min((100 * height) / width, 100)}%`,
                              maxHeight: `423px`,
                              height: '0',
                            }
                          : {}
                      }
                      src={(() => {
                        // no need to use lazysizes as this can only happen once the image is loaded
                        if (cropper.imgHasBeenUploaded && mainImage) {
                          return mainImage.url
                        }

                        // leverage lazysizes, use white bg as a placeholder
                        if (mainImage) {
                          return whitePixel
                        }

                        // if the product has no image at all
                        return placeholderImage.url
                      })()}
                      data-src={mainImage?.url || undefined}
                      alt={product.title}
                      className={`${
                        mainImage?.url ? 'lazyload' : ''
                      } rounded-lg ${placeholderClasses} max-w-full mx-auto`}
                    />
                  )
                })()}
              </LinkWithoutPrefetch>
              <style jsx>
                {'.lazyloaded { padding-top: 0px !important; height: auto !important; }'}
              </style>
            </div>
          )
        })()}
      </div>

      {orderTypeHasPaymentMethods ? (
        <div
          className="flex items-center justify-between mt-2"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <LinkWithoutPrefetch {...productLink}>
            <a
              className={`${
                showTwoProductsInRow && props.allowMovePriceToOrderButton
                  ? 'hidden lg:flex'
                  : 'flex'
              } justify-center items-center text-lg w-40 h-12 text-center focus:outline-none`}
            >
              <EyeIcon />
              <span className="ml-2">{t('common:details')}</span>
            </a>
          </LinkWithoutPrefetch>

          {
            <div
              className={
                showTwoProductsInRow && props.allowMovePriceToOrderButton
                  ? 'w-[100%] lg:w-[45%]'
                  : 'w-[45%]'
              }
            >
              <OrderButtons
                counterStyle="action"
                quantity={props.orderedQuantity}
                onQuantityChange={props.onQuantityChange}
                product={product}
                allowMovePriceToOrderButton={props.allowMovePriceToOrderButton}
              />
            </div>
          }
        </div>
      ) : (
        showTwoProductsInRow && (
          <div className="flex mt-2 lg:hidden">
            <div
              className={`h-12 text-center align-middle  text-lg border border-action dark:border-actionDark rounded w-full ${
                props.orderedQuantity > 0 ? 'bg-action dark:bg-actionDark' : 'bg-inherit'
              }`}
            >
              <button
                className="flex items-center justify-center w-full h-full focus:outline-none"
                onClick={(e) => {
                  if (disableButton) {
                    return
                  }

                  if (props.orderedQuantity) {
                    const basketProduct = basket.products.filter((p) => {
                      return (
                        p.productGroupName === product.productGroupName &&
                        p.productName === product.name &&
                        p.quantity > 0
                      )
                    })[0]

                    basketProduct && basket.products.deleteById(basketProduct.__id)
                  } else {
                    props.onQuantityChange(product.minQuantity, '+', e)
                  }
                }}
              >
                {props.orderedQuantity ? (
                  <>
                    <SlimCheckmark color="stroke-back dark:stroke-backDark" />
                    <span className="ml-1 text-back dark:text-backDark">
                      {t('common:selected')}
                    </span>
                  </>
                ) : (
                  price.format(product.price)
                )}
              </button>
            </div>
          </div>
        )
      )}
    </div>
  )
}

export type CookingTimeProps = {
  cookingTimeMin?: number
}

export function CookingTime(props: CookingTimeProps) {
  const { t } = useAppContext()
  const venueContext = useVenueContext()!
  const { orderManager } = venueContext
  const currentOrderMode = orderManager.orderMode

  if (currentOrderMode.value !== 'onSite' || !props.cookingTimeMin) {
    return null
  }
  return (
    <div className="mr-2">
      <div className="w-4 h-4 inline-block align-middle pb-0.5">
        <ClockIcon stroke="stroke-opaque dark:stroke-opaqueDark" />
      </div>
      <span>
        {props.cookingTimeMin} {t('common:minutesShort')}
      </span>
    </div>
  )
}

export function LinkWithoutPrefetch(
  props: PropsWithChildren<{
    href: string
    scroll?: boolean
    handleClick?: (e: MouseEvent) => boolean
  }>,
) {
  const router = useRouter()

  return (
    <a
      href={props.href}
      onClick={(e) => {
        e.preventDefault()

        if (props.handleClick && !props.handleClick(e)) {
          return
        }

        router.push(props.href, props.href, {
          scroll: props.scroll,
        })
      }}
      className="h-full"
    >
      {props.children}
    </a>
  )
}
